function joinBasePath(baseURL, path) {
  if (/^https?:\/\//i.test(path)) {
    return path;
  }
  const base = String(baseURL || '').replace(/\/+$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

function appendParams(urlString, params) {
  if (!params || typeof params !== 'object' || !Object.keys(params).length) {
    return urlString;
  }
  const url = new URL(urlString);
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (Array.isArray(value)) {
      value.forEach((v) => url.searchParams.append(key, String(v)));
    } else {
      url.searchParams.append(key, String(value));
    }
  });
  return url.toString();
}

function mergeHeaders(...parts) {
  const out = {};
  parts.forEach((h) => {
    if (!h) return;
    Object.entries(h).forEach(([k, v]) => {
      if (v !== undefined && v !== null) out[k] = String(v);
    });
  });
  return out;
}

function prepareBody(data, headers) {
  if (data === undefined || data === null) {
    return { body: undefined };
  }
  if (typeof FormData !== 'undefined' && data instanceof FormData) {
    delete headers['Content-Type'];
    return { body: data };
  }
  if (data instanceof URLSearchParams) {
    if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/x-www-form-urlencoded;charset=UTF-8';
    }
    return { body: data.toString() };
  }
  if (typeof data === 'string' || data instanceof Blob) {
    return { body: data };
  }
  headers['Content-Type'] =
    headers['Content-Type'] || 'application/json;charset=UTF-8';
  return { body: JSON.stringify(data) };
}

async function parseResponseData(response) {
  const ct =
    (response.headers && response.headers.get && response.headers.get('content-type')) ||
    '';
  const text = await response.text();
  if (!text) return null;
  if (ct.includes('application/json') || ct.includes('application/problem+json')) {
    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }
  return text;
}

function createHttpError({ message, status, data, config }) {
  const error = new Error(message);
  error.response = { status, statusText: '', data, headers: {} };
  error.config = config;
  return error;
}

function createNetworkError(originalError, config) {
  const error = new Error(originalError?.message || 'Network Error');
  error.code = 'ERR_NETWORK';
  error.config = config;
  error.response = undefined;
  return error;
}

/**
 * @param {object} options
 * @param {string} options.baseURL
 * @param {() => Record<string, string>} [options.getAuthHeaders]
 * @param {() => void} [options.afterSuccess]
 * @param {(ctx: { status?: number, isNetwork: boolean, requestUrl: string }) => void} [options.afterError]
 */
export function createHttpClient({
  baseURL,
  getAuthHeaders = () => ({}),
  afterSuccess = () => {},
  afterError = () => {},
}) {
  async function coreRequest(method, path, { data: bodyArg, config: userConfig }) {
    const cfg = userConfig || {};
    const { params, headers: cfgHeaders, data: deletePayload, ...restCfg } = cfg;
    const url = appendParams(joinBasePath(baseURL, path), params);
    const hdrCopy = mergeHeaders(getAuthHeaders(), cfgHeaders || {});

    let entityBody;
    if (method === 'GET' || method === 'HEAD') {
      entityBody = undefined;
    } else if (method === 'DELETE') {
      entityBody = deletePayload;
    } else {
      entityBody = bodyArg;
    }

    const prep =
      entityBody === undefined ? { body: undefined } : prepareBody(entityBody, hdrCopy);
    const body = prep.body;

    const requestConfig = { url, method, path, ...restCfg, params, headers: cfgHeaders };

    try {
      const response = await fetch(url, {
        method,
        headers: hdrCopy,
        body: body === undefined ? undefined : body,
      });

      if (!response.ok) {
        let responseData;
        try {
          responseData = await parseResponseData(response);
        } catch {
          responseData = null;
        }
        afterError({
          status: response.status,
          isNetwork: false,
          requestUrl: url,
        });
        throw createHttpError({
          message: `Request failed with status code ${response.status}`,
          status: response.status,
          data: responseData,
          config: requestConfig,
        });
      }

      afterSuccess();

      const parsed = await parseResponseData(response);
      return {
        data: parsed,
        status: response.status,
        statusText: response.statusText,
        headers: response.headers,
        config: requestConfig,
      };
    } catch (err) {
      if (err.response) {
        throw err;
      }
      afterError({ isNetwork: true, requestUrl: url });
      throw createNetworkError(err, requestConfig);
    }
  }

  return {
    get: (path, config) => coreRequest('GET', path, { config: config || {} }),
    post: (path, data, config) =>
      coreRequest('POST', path, { data, config: config || {} }),
    put: (path, data, config) =>
      coreRequest('PUT', path, { data, config: config || {} }),
    patch: (path, data, config) =>
      coreRequest('PATCH', path, { data, config: config || {} }),
    delete: (path, config) => coreRequest('DELETE', path, { config: config || {} }),
  };
}
